import { graphql, useStaticQuery } from "gatsby"

import { forEach, isArray, keyBy } from "lodash"
import { useMemo } from "react"
import { Helmet } from "react-helmet"

/**
 * @typedef {Object} HeadProps
 * @property {string=} title
 * @property {string=} description
 * @property {any=} image
 * @property {Object<string, string>=} metas
 * @property {string=} url
 * @property {string=} type
 */

/**
 * Head component.
 * @param {HeadProps} props
 * @return {JSX.Element}
 */
export default function Head({ title, description, image, metas, url = "", type = "website" }) {
  const metasByName = useMemo(() => keyBy(metas, "name"), [metas])
  const Metas = useMemo(() => {
    if (!isArray(metas)) return null
    // Using lodash loop metatag items and return composed meta tags elements where
    // name is the meta name and content is the meta value.
    const MetaElements = []
    forEach(metas, (metaItem) => {
      const metaName = metaItem?.name
      const metaProperty = metaItem?.property
      const metaValue = metaItem?.content

      // Meta with name.
      if (metaName && metaValue) {
        MetaElements.push(<meta key={metaName} name={metaName} content={metaValue} />)
      }

      // Meta with property.
      if (!metaName && metaProperty && metaValue) {
        MetaElements.push(<meta key={metaProperty} property={metaProperty} content={metaValue} />)
      }
    })

    return MetaElements
  }, [metas])

  const data = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)

  const siteUrl = process.env.SITE_URL || data.site.siteMetadata.siteUrl || "https://www.goway.com"

  const getCanonicalUrl = () => {
    if (!url && typeof window !== "undefined") {
      return `${siteUrl}${window.location.pathname}`
    }
    return `${siteUrl}${url}`
  }

  const canonicalUrl = getCanonicalUrl()
  const finalDescription = description || metasByName?.description?.content || ""
  const finalTitle = title || metasByName?.title?.content || "Goway"
  const finalImage = metasByName?.image?.url || image
  return (
    <Helmet>
      <title>{finalTitle}</title>
      {finalDescription && <meta property="og:description" content={finalDescription} />}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      {finalImage && <meta property="og:image" content={finalImage} />}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={finalTitle} />
      {finalDescription && <meta name="twitter:description" content={finalDescription} />}
      <meta name="google-site-verification" content="5244fq1d-cvPmbdwT7vYaEQra6bAW5dSxeHoy7i7G1U" />
      {finalImage && <meta name="twitter:image" content={finalImage} />}

      <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#f05b2a" />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="msapplication-TileColor" content="#da532c" />
      <meta name="theme-color" content="#ffffff" />

      {/* <!-- TrustBox script --> */}
      <script
        type="text/javascript"
        src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        async
      />
      {/* <!-- End TrustBox script --> */}
      {Metas}
    </Helmet>
  )
}

export const query = graphql`
  fragment MetasContent on field__meta_tags {
    author
    abstract
    geo_placename
    image_src
    keywords
    news_keywords
    robots
  }
`
